import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Building2, User, Users, Printer, FileText, Filter, ChevronRight, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STATUSES = ['all', 'paid', 'due', 'overdue', 'active', 'closed'];

const openPrint = (html) => {
  const w = window.open('', '_blank', 'width=1000,height=800');
  if (!w) { toast.error('Popup blocked'); return; }
  w.document.write(`<!DOCTYPE html><html><head><title>Report</title>
    <style>body{font-family:Helvetica,Arial,sans-serif;color:#0F172A;margin:24px}
    h1{font-size:22px;margin:0 0 4px} h2{font-size:14px;margin:0 0 12px;color:#D97706}
    .filters{background:#F8FAFC;border:1px solid #E2E8F0;padding:8px 12px;border-radius:4px;margin-bottom:12px;font-size:12px}
    table{width:100%;border-collapse:collapse;font-size:11px}
    th{background:#0F172A;color:#fff;text-align:left;padding:6px 8px}
    td{border:1px solid #E2E8F0;padding:5px 8px}
    tfoot td{background:#FEF3C7;font-weight:bold}
    @media print{@page{size:A4 landscape;margin:12mm}}
    </style></head><body>${html}<script>window.onload=()=>window.print()</script></body></html>`);
  w.document.close();
};

export default function PerformanceCenter() {
  const { user } = useAuth();
  const isHQ = user?.role === 'Super Admin' || user?.role === 'Admin';
  const isManager = user?.role === 'Branch Manager';
  const [tab, setTab] = useState('performance');   // performance | customers
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({
    branch_id: isManager ? (user?.branch_id || '') : '',
    manager_id: '', user_id: '', status: 'all', date_from: '', date_to: '',
  });
  const [summary, setSummary] = useState(null);
  const [customers, setCustomers] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/branches`, { withCredentials: true }).then(r => setBranches(r.data || [])).catch(() => {});
    axios.get(`${API_URL}/api/users`, { withCredentials: true }).then(r => setUsers(r.data || [])).catch(() => {});
  }, []);

  const usersInScope = useMemo(() => {
    return users.filter(u => (!filters.branch_id || u.branch_id === filters.branch_id));
  }, [users, filters.branch_id]);

  const loadSummary = async () => {
    setLoading(true);
    try {
      const endpoint = isHQ ? 'summary' : 'team';
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const { data } = await axios.get(`${API_URL}/api/performance/${endpoint}?${params.toString()}`, { withCredentials: true });
      setSummary(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load performance'); }
    finally { setLoading(false); }
  };

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v && k !== 'manager_id') params.append(k, v); });
      const { data } = await axios.get(`${API_URL}/api/performance/customers-status?${params.toString()}`, { withCredentials: true });
      setCustomers(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load customers'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (tab === 'performance') loadSummary(); else loadCustomers(); /* eslint-disable-next-line */ }, [tab]);

  const filterSummary = () => {
    const parts = [];
    if (filters.branch_id) parts.push(`Branch: ${branches.find(b => b.id === filters.branch_id)?.name || filters.branch_id}`);
    if (filters.user_id) parts.push(`User: ${users.find(u => u.id === filters.user_id)?.name || filters.user_id}`);
    if (filters.status && filters.status !== 'all') parts.push(`Status: ${filters.status}`);
    if (filters.date_from) parts.push(`From: ${filters.date_from}`);
    if (filters.date_to) parts.push(`To: ${filters.date_to}`);
    return parts.length ? parts.join(' · ') : 'No filters — full data';
  };

  const printPerformance = () => {
    if (!summary) return;
    const level = summary.users?.length ? 'users' : summary.managers?.length ? 'managers' : 'branches';
    const rows = summary[level] || [];
    const headers = ['Name', level === 'branches' ? 'Manager' : level === 'users' ? 'Role' : 'Branch',
      'Customers', 'Paid', 'Due', 'Overdue', 'Active', 'Closed', 'Total Sales', 'Collections', 'Pending', 'Overdue Amt', 'Business Profit'];
    const body = rows.map(r => `<tr><td>${r.name}</td><td>${r.manager_name || r.role || r.branch_id || '—'}</td>
      <td>${r.total_customers}</td><td>${r.paid_customers}</td><td>${r.due_customers}</td><td>${r.overdue_customers}</td>
      <td>${r.active_customers}</td><td>${r.closed_customers}</td><td>PKR ${(r.total_sales||0).toLocaleString()}</td>
      <td>PKR ${(r.total_collections||0).toLocaleString()}</td><td>PKR ${(r.pending_amount||0).toLocaleString()}</td>
      <td>PKR ${(r.overdue_amount||0).toLocaleString()}</td>
      <td>PKR ${(r.business_profit||0).toLocaleString()}</td></tr>`).join('');
    const t = summary.totals || {};
    openPrint(`<h1>MANHA DIGITAL — Performance Report</h1><h2>Generated ${new Date().toLocaleString('en-GB')}</h2>
      <div class="filters"><b>Filters:</b> ${filterSummary()}</div>
      <table><thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>
      <tbody>${body || '<tr><td colspan="13" style="text-align:center">No rows</td></tr>'}</tbody>
      <tfoot><tr><td colspan="2">TOTALS</td>
      <td>${t.total_customers||0}</td><td>${t.paid_customers||0}</td><td>${t.due_customers||0}</td><td>${t.overdue_customers||0}</td>
      <td>${t.active_customers||0}</td><td>${t.closed_customers||0}</td>
      <td>PKR ${(t.total_sales||0).toLocaleString()}</td><td>PKR ${(t.total_collections||0).toLocaleString()}</td>
      <td>PKR ${(t.pending_amount||0).toLocaleString()}</td><td>PKR ${(t.overdue_amount||0).toLocaleString()}</td>
      <td>PKR ${(t.business_profit||0).toLocaleString()}</td>
      </tr></tfoot></table>`);
  };

  const printCustomers = () => {
    if (!customers) return;
    const headers = ['Customer', 'Account', 'Branch', 'Manager', 'Assigned', 'Product', 'Agr #', 'Agr Date',
      'Installment', 'Paid', 'Remaining', 'Status'];
    const body = customers.rows.map(r => `<tr><td>${r.customer_name}</td><td>${r.account_number}</td>
      <td>${r.branch}</td><td>${r.manager}</td><td>${r.assigned_user}</td><td>${r.product || '—'}</td>
      <td>${r.agreement_number || '—'}</td>
      <td>${r.agreement_date ? new Date(r.agreement_date).toLocaleDateString('en-GB') : '—'}</td>
      <td>PKR ${(r.installment_amount||0).toLocaleString()}</td>
      <td>PKR ${(r.paid_amount||0).toLocaleString()}</td>
      <td>PKR ${(r.remaining_amount||0).toLocaleString()}</td>
      <td style="text-transform:uppercase">${r.status}</td></tr>`).join('');
    const t = customers.totals || {};
    openPrint(`<h1>MANHA DIGITAL — Customer Report (${filters.status.toUpperCase()})</h1>
      <h2>Generated ${new Date().toLocaleString('en-GB')}</h2>
      <div class="filters"><b>Filters:</b> ${filterSummary()}</div>
      <table><thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>
      <tbody>${body || '<tr><td colspan="12" style="text-align:center">No rows</td></tr>'}</tbody>
      <tfoot><tr><td colspan="8">TOTALS · ${t.customers} customers</td>
      <td></td><td>PKR ${(t.paid||0).toLocaleString()}</td><td>PKR ${(t.remaining||0).toLocaleString()}</td><td></td>
      </tr></tfoot></table>`);
  };

  return (
    <div className="space-y-6" data-testid="performance-center">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-1" data-testid="performance-title">Performance & Reports</h1>
          <p className="text-slate-400 text-sm">{isHQ ? 'Branch → Manager → User drill-down.' : 'Your team performance and customer reports.'}</p>
        </div>
        <div className="flex space-x-2">
          <Button data-testid="tab-performance" variant={tab === 'performance' ? 'default' : 'ghost'}
            onClick={() => setTab('performance')} className={tab === 'performance' ? 'bg-amber-500 hover:bg-amber-600' : 'text-slate-300'}>
            <Users className="h-4 w-4 mr-2" /> Performance
          </Button>
          <Button data-testid="tab-customers" variant={tab === 'customers' ? 'default' : 'ghost'}
            onClick={() => setTab('customers')} className={tab === 'customers' ? 'bg-amber-500 hover:bg-amber-600' : 'text-slate-300'}>
            <FileText className="h-4 w-4 mr-2" /> Customer Reports
          </Button>
        </div>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-white text-base flex items-center"><Filter className="h-4 w-4 mr-2 text-amber-500" /> Filters</CardTitle>
          <div className="flex items-center space-x-2">
            <Button onClick={tab === 'performance' ? printPerformance : printCustomers} data-testid="print-report"
              className="bg-emerald-600 hover:bg-emerald-700"><Printer className="h-4 w-4 mr-2" /> Print</Button>
          </div>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {isHQ && (
            <div className="space-y-1">
              <Label className="text-slate-400 text-xs">Branch</Label>
              <select value={filters.branch_id} data-testid="filter-branch"
                onChange={(e) => setFilters({ ...filters, branch_id: e.target.value, manager_id: '', user_id: '' })}
                className="w-full bg-slate-800 border border-slate-700 text-white rounded-sm h-9 px-2 text-xs">
                <option value="">All Branches</option>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          )}
          <div className="space-y-1">
            <Label className="text-slate-400 text-xs">User</Label>
            <select value={filters.user_id} data-testid="filter-user"
              onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}
              className="w-full bg-slate-800 border border-slate-700 text-white rounded-sm h-9 px-2 text-xs">
              <option value="">All Users</option>
              {usersInScope.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-slate-400 text-xs">Status</Label>
            <select value={filters.status} data-testid="filter-status"
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full bg-slate-800 border border-slate-700 text-white rounded-sm h-9 px-2 text-xs">
              {STATUSES.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-slate-400 text-xs">From Date</Label>
            <Input type="date" data-testid="filter-date-from" value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              className="bg-slate-800 border-slate-700 text-white h-9 text-xs" />
          </div>
          <div className="space-y-1">
            <Label className="text-slate-400 text-xs">To Date</Label>
            <Input type="date" data-testid="filter-date-to" value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              className="bg-slate-800 border-slate-700 text-white h-9 text-xs" />
          </div>
          <div className="space-y-1 flex items-end">
            <Button onClick={tab === 'performance' ? loadSummary : loadCustomers}
              data-testid="apply-filters"
              disabled={loading}
              className="bg-amber-500 hover:bg-amber-600 w-full">Apply</Button>
          </div>
        </CardContent>
      </Card>

      {tab === 'performance' ? (
        <PerformanceGrid summary={summary} loading={loading} filters={filters} setFilters={setFilters} isHQ={isHQ} />
      ) : (
        <CustomersGrid customers={customers} loading={loading} />
      )}
    </div>
  );
}

function PerformanceGrid({ summary, loading, filters, setFilters, isHQ }) {
  if (loading) return <div className="text-slate-400 py-8 text-center">Loading…</div>;
  if (!summary) return null;
  const totals = summary.totals || {};
  const level = summary.users?.length ? 'users' : summary.managers?.length ? 'managers' : 'branches';
  const rows = summary[level] || [];

  return (
    <>
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader><CardTitle className="text-white text-base">Totals for current filter</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            ['Customers', totals.total_customers], ['Paid', totals.paid_customers],
            ['Due', totals.due_customers], ['Overdue', totals.overdue_customers],
            ['Active', totals.active_customers], ['Closed', totals.closed_customers],
            ['Total Sales', `PKR ${(totals.total_sales || 0).toLocaleString()}`],
            ['Collections', `PKR ${(totals.total_collections || 0).toLocaleString()}`],
            ['Pending', `PKR ${(totals.pending_amount || 0).toLocaleString()}`],
            ['Overdue Amount', `PKR ${(totals.overdue_amount || 0).toLocaleString()}`],
            ['Business Profit', `PKR ${(totals.business_profit || 0).toLocaleString()}`],
          ].map(([k, v]) => (
            <div key={k} className={`bg-slate-800/60 border border-slate-700 rounded-sm p-3 ${k === 'Business Profit' ? 'border-emerald-500/50 bg-emerald-900/20' : ''}`}>
              <div className={`text-xs ${k === 'Business Profit' ? 'text-emerald-300' : 'text-slate-400'}`}>{k}</div>
              <div className={`text-lg font-semibold ${k === 'Business Profit' ? 'text-emerald-300' : 'text-white'}`} data-testid={k === 'Business Profit' ? 'perf-total-profit' : undefined}>{v ?? 0}</div>
            </div>
          ))}
        </CardContent>
      </Card>

      {isHQ && filters.branch_id && (
        <Button variant="ghost" data-testid="clear-drill" onClick={() => setFilters({ ...filters, branch_id: '', manager_id: '', user_id: '' })}
          className="text-slate-400 hover:text-white"><ArrowLeft className="h-4 w-4 mr-1" /> Back to All Branches</Button>
      )}

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader><CardTitle className="text-white text-base capitalize">{level} ({rows.length})</CardTitle></CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <div className="text-slate-500 text-center py-6">No rows.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800">
                    <TableHead className="text-slate-400">Name</TableHead>
                    <TableHead className="text-slate-400">{level === 'branches' ? 'Manager' : level === 'users' ? 'Role' : 'Branch'}</TableHead>
                    <TableHead className="text-slate-400">Customers</TableHead>
                    <TableHead className="text-slate-400">Paid</TableHead>
                    <TableHead className="text-slate-400">Due</TableHead>
                    <TableHead className="text-slate-400">Overdue</TableHead>
                    <TableHead className="text-slate-400">Sales</TableHead>
                    <TableHead className="text-slate-400">Collections</TableHead>
                    <TableHead className="text-slate-400">Pending</TableHead>
                    <TableHead className="text-emerald-400">Profit</TableHead>
                    {isHQ && level === 'branches' && <TableHead className="text-slate-400">Drill</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id} className="border-slate-800 hover:bg-slate-800/40" data-testid={`perf-row-${r.id}`}>
                      <TableCell className="text-white">{r.name}</TableCell>
                      <TableCell className="text-slate-300 text-xs">{r.manager_name || r.role || r.branch_id || '—'}</TableCell>
                      <TableCell className="text-slate-300">{r.total_customers}</TableCell>
                      <TableCell className="text-emerald-500">{r.paid_customers}</TableCell>
                      <TableCell className="text-amber-500">{r.due_customers}</TableCell>
                      <TableCell className="text-red-500">{r.overdue_customers}</TableCell>
                      <TableCell className="text-slate-300">PKR {(r.total_sales || 0).toLocaleString()}</TableCell>
                      <TableCell className="text-emerald-400">PKR {(r.total_collections || 0).toLocaleString()}</TableCell>
                      <TableCell className="text-yellow-500">PKR {(r.pending_amount || 0).toLocaleString()}</TableCell>
                      <TableCell className="text-emerald-300 font-semibold" data-testid={`perf-profit-${r.id}`}>PKR {(r.business_profit || 0).toLocaleString()}</TableCell>
                      {isHQ && level === 'branches' && (
                        <TableCell>
                          <Button size="sm" variant="ghost" onClick={() => setFilters({ ...filters, branch_id: r.id })}
                            data-testid={`drill-${r.id}`} className="h-7 px-2 text-cyan-400"><ChevronRight className="h-4 w-4" /></Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}

function CustomersGrid({ customers, loading }) {
  if (loading) return <div className="text-slate-400 py-8 text-center">Loading…</div>;
  if (!customers) return null;
  return (
    <Card className="bg-slate-900 border-slate-800">
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="text-white text-base">Customer Report ({customers.count})</CardTitle>
        <div className="text-xs text-slate-400">
          Paid PKR {(customers.totals?.paid || 0).toLocaleString()} · Remaining PKR {(customers.totals?.remaining || 0).toLocaleString()}
        </div>
      </CardHeader>
      <CardContent>
        {customers.rows.length === 0 ? (
          <div className="text-slate-500 text-center py-6">No customers match your filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800">
                  <TableHead className="text-slate-400">Customer</TableHead>
                  <TableHead className="text-slate-400">Account #</TableHead>
                  <TableHead className="text-slate-400">Branch</TableHead>
                  <TableHead className="text-slate-400">Manager</TableHead>
                  <TableHead className="text-slate-400">Assigned</TableHead>
                  <TableHead className="text-slate-400">Product</TableHead>
                  <TableHead className="text-slate-400">Agr Date</TableHead>
                  <TableHead className="text-slate-400">Installment</TableHead>
                  <TableHead className="text-slate-400">Paid</TableHead>
                  <TableHead className="text-slate-400">Remaining</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customers.rows.map((r, i) => (
                  <TableRow key={i} className="border-slate-800 hover:bg-slate-800/40" data-testid="customer-report-row">
                    <TableCell className="text-white">{r.customer_name}</TableCell>
                    <TableCell className="text-slate-300 text-xs">{r.account_number}</TableCell>
                    <TableCell className="text-slate-300 text-xs">{r.branch}</TableCell>
                    <TableCell className="text-slate-300 text-xs">{r.manager}</TableCell>
                    <TableCell className="text-slate-300 text-xs">{r.assigned_user}</TableCell>
                    <TableCell className="text-slate-300 text-xs">{r.product || '—'}</TableCell>
                    <TableCell className="text-slate-300 text-xs">{r.agreement_date ? new Date(r.agreement_date).toLocaleDateString('en-GB') : '—'}</TableCell>
                    <TableCell className="text-slate-300">PKR {(r.installment_amount || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-emerald-500">PKR {(r.paid_amount || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-amber-500">PKR {(r.remaining_amount || 0).toLocaleString()}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 text-xs uppercase rounded-sm ${
                        r.status === 'paid' ? 'bg-emerald-500/20 text-emerald-500' :
                        r.status === 'overdue' ? 'bg-red-500/20 text-red-500' :
                        r.status === 'due' ? 'bg-amber-500/20 text-amber-500' :
                        r.status === 'closed' ? 'bg-slate-500/20 text-slate-300' :
                        'bg-cyan-500/20 text-cyan-400'
                      }`}>{r.status}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
